import {
  CheckInput,
  Icon,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import {
  Fragment,
  ReactChild,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Control, Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, FormText, Row } from 'reactstrap';
import {
  AcademicLevelListItem,
  SchoolsListItem,
} from '../../../../api/requests/schools';
import { SectionTitle } from '../../../../components/text';
import Tooltip from '../../../../components/tooltip/Tooltip';
import { useLayoutState } from '../../../../hooks/useLayoutState';
import { useValidations } from '../../../../hooks/useValidations';
import { ModalityListItem } from '../../../../types/ModalityTypes';
import { PeriodType } from '../../../../types/PeriodType';
import { formatOptions } from '../../../../utils/selectFormat';
import { StudyPlanErrorCode } from '../api/study-plan-errors';
import StudyPlanFormSies from './StudyPlanFormSies';
import styles from './TitlesAndDegreesSection.module.scss';

export interface StudyPlanValues {
  code: string;
  siesCode: string;
  demreCode: string;
  name: string;
  degreeTitle: string | null;
  academicDegree: string | null;
  degreeTitleFemale: string | null;
  degreeTitleMale: string | null;
  academicDegreeFemale: string | null;
  academicDegreeMale: string | null;
  noTitleObtained: boolean;
  noAcademicDegree: boolean;
  school: SelectOptionType | null;
  modality: SelectOptionType | null;
  periodType: SelectOptionType | null;
  academicLevel: SelectOptionType | null;
  careerLevel: SelectOptionType | null;
  hasLaw20903CLRequirement: SelectOptionType | null;
  // SIES
  admissionRequirement?: SelectOptionType | null;
  recognizedSemesters?: number;
  academicArea?: SelectOptionType | null;
  highSchoolDiplomaAdmission?: SelectOptionType | null;
  highSchoolGradesAdmission?: SelectOptionType | null;
  minimumHighSchoolGPA?: number;
  priorLearningRecognitionAdmission?: SelectOptionType | null;
  workExperienceAdmission?: SelectOptionType | null;
}

export interface RecordToEditBody {
  code: string;
  siesCode: string;
  demreCode?: string | null;
  name: string;
  degreeTitle: string | null;
  academicDegree: string | null;
  degreeTitleFemale?: string | null;
  degreeTitleMale?: string | null;
  academicDegreeFemale?: string | null;
  academicDegreeMale?: string | null;
  school: SelectOptionType;
  modality: SelectOptionType;
  periodType: SelectOptionType;
  academicLevel: SelectOptionType;
  careerLevel: SelectOptionType | null;
  hasLaw20903CLRequirement?: SelectOptionType | null;
  academicArea?: SelectOptionType | null;
  recognizedSemesters?: number;
  admissionRequirement?: SelectOptionType | null;
  highSchoolDiplomaAdmission?: SelectOptionType | null;
  highSchoolGradesAdmission?: SelectOptionType | null;
  minimumHighSchoolGPA?: number;
  priorLearningRecognitionAdmission?: SelectOptionType | null;
  workExperienceAdmission?: SelectOptionType | null;
}

const getDefaultValues = (defaultValues?: RecordToEditBody) =>
  defaultValues
    ? {
        code: defaultValues.code,
        siesCode: defaultValues.siesCode?.trim() || '',
        demreCode: defaultValues.demreCode?.trim() || '',
        name: defaultValues.name,
        school: defaultValues.school,
        modality: defaultValues.modality,
        periodType: defaultValues.periodType,
        academicLevel: defaultValues.academicLevel,
        careerLevel: defaultValues.careerLevel,
        hasLaw20903CLRequirement:
          defaultValues.hasLaw20903CLRequirement ?? null,
        degreeTitle: defaultValues.degreeTitle,
        academicDegree: defaultValues.academicDegree,
        degreeTitleFemale: defaultValues.degreeTitleFemale ?? '',
        degreeTitleMale: defaultValues.degreeTitleMale ?? '',
        academicDegreeFemale: defaultValues.academicDegreeFemale ?? '',
        academicDegreeMale: defaultValues.academicDegreeMale ?? '',
        noTitleObtained: defaultValues.degreeTitle == null,
        noAcademicDegree: defaultValues.academicDegree == null,
        academicArea: defaultValues.academicArea,
        recognizedSemesters: defaultValues.recognizedSemesters,
        admissionRequirement: defaultValues.admissionRequirement,
        highSchoolDiplomaAdmission: defaultValues.highSchoolDiplomaAdmission,
        highSchoolGradesAdmission: defaultValues.highSchoolGradesAdmission,
        minimumHighSchoolGPA: defaultValues.minimumHighSchoolGPA,
        priorLearningRecognitionAdmission:
          defaultValues.priorLearningRecognitionAdmission,
        workExperienceAdmission: defaultValues.workExperienceAdmission,
      }
    : {
        code: '',
        siesCode: '',
        demreCode: '',
        name: '',
        degreeTitle: '',
        academicDegree: '',
        degreeTitleFemale: '',
        degreeTitleMale: '',
        academicDegreeFemale: '',
        academicDegreeMale: '',
        noTitleObtained: false,
        noAcademicDegree: false,
        school: null,
        modality: null,
        periodType: null,
        academicLevel: null,
        careerLevel: null,
        hasLaw20903CLRequirement: null,
        academicArea: null,
        recognizedSemesters: 0,
        admissionRequirement: null,
        highSchoolDiplomaAdmission: null,
        highSchoolGradesAdmission: null,
        minimumHighSchoolGPA: 0,
        priorLearningRecognitionAdmission: null,
        workExperienceAdmission: null,
      };

export interface StudyPlanFormProps {
  modalities: ModalityListItem[];
  periodTypes: PeriodType[];
  schools: SchoolsListItem[];
  academicLevels: AcademicLevelListItem[];
  academicAreas: AcademicLevelListItem[];
  admissionRequirements: AcademicLevelListItem[];
  defaultValues?: RecordToEditBody;
  children: (props: { isSubmitting: boolean; isDirty?: boolean }) => ReactChild;
  onSubmit: (
    values: StudyPlanValues,
  ) => Promise<'CONNECTION' | StudyPlanErrorCode | undefined>;
  readOnly?: boolean;
  isUpdate?: boolean;
  siesEnabled?: boolean;
  demreEnabled?: boolean;
}

type GenderedFieldName =
  | 'degreeTitle'
  | 'degreeTitleFemale'
  | 'degreeTitleMale'
  | 'academicDegree'
  | 'academicDegreeFemale'
  | 'academicDegreeMale';

interface GenderedInputCellProps {
  name: GenderedFieldName;
  control: Control<StudyPlanValues>;
  disabled?: boolean;
  validate?: (value: string | null) => true | string;
}

/**
 * Input de la celda "Género" de la tabla de títulos/grados.
 * Sin label flotante: la etiqueta del campo es el texto del género en la
 * columna izquierda de la fila, por lo que se usa un <input> plano
 * conectado a react-hook-form vía Controller (en vez del TextInput de
 * @octano/global-ui, que siempre renderiza un label flotante).
 */
const GenderedInputCell = ({
  name,
  control,
  disabled,
  validate,
}: GenderedInputCellProps) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate,
        maxLength: {
          value: 255,
          message: t('common.validations.maxLength', { length: 255 }),
        },
      }}
      render={({ field, fieldState }) => (
        <>
          <input
            {...field}
            value={field.value ?? ''}
            maxLength={255}
            disabled={disabled}
            className={`form-control ${styles.tadInput}`}
          />
          {fieldState.error?.message && (
            <FormText color="danger" className={styles.tadError}>
              {fieldState.error.message}
            </FormText>
          )}
        </>
      )}
    />
  );
};

// Los codigos de plan y version admiten guion y guion bajo ademas de
// alfanumericos: hay datos vivos que ya los usan (p.ej. MAG-TEST / MAG-TEST-V1)
// y con la regla solo-alfanumerica esos registros no se podian guardar.
// Debe coincidir con el @Matches de Create/UpdateStudyPlanDto.
const CODE_PATTERN = /^[0-9a-zA-Z_-]+$/;

const StudyPlanForm = (props: StudyPlanFormProps) => {
  const {
    onSubmit: onSubmitProp,
    children,
    modalities,
    periodTypes,
    schools,
    academicLevels,
    academicAreas,
    admissionRequirements,
    defaultValues,
    readOnly,
    isUpdate,
    siesEnabled,
    demreEnabled,
  } = props;

  const { showErrorModal } = useLayoutState();

  const { t } = useTranslation();
  const prefix = 'studyPlans.form';

  // "Codigo del plan" siempre esta; SIES y Demre entran segun su propio switch.
  // Si la cuenta es par, la fila ya cerro en 12 y "Nombre" arranca una nueva a
  // lo ancho; si es impar, "Nombre" completa la mitad que quedo libre.
  const codeColumnsUsed = 1 + (siesEnabled ? 1 : 0) + (demreEnabled ? 1 : 0);

  const lawRequirementOptions: SelectOptionType[] = [
    { label: t(`${prefix}.yes`), value: 1 },
    { label: t(`${prefix}.no`), value: 0 },
  ];

  const methods = useForm<StudyPlanValues>({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(defaultValues),
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    control,
    setError,
    setValue,
    watch,
  } = methods;

  const [selectedAcademicLevel, noTitleObtained, noAcademicDegree] = watch([
    'academicLevel',
    'noTitleObtained',
    'noAcademicDegree',
  ]);
  const [careerLevels, setFilteredCareerLevels] = useState<SelectOptionType[]>(
    [],
  );

  useEffect(() => {
    if (noTitleObtained) {
      setValue('degreeTitle', null);
      setValue('degreeTitleFemale', null);
      setValue('degreeTitleMale', null);
    }
  }, [noTitleObtained, setValue]);

  useEffect(() => {
    if (noAcademicDegree) {
      setValue('academicDegree', null);
      setValue('academicDegreeFemale', null);
      setValue('academicDegreeMale', null);
    }
  }, [noAcademicDegree, setValue]);

  const { msgValidations } = useValidations();
  const onSubmit = useCallback(
    async (formValues: StudyPlanValues) => {
      const response = await onSubmitProp(formValues);
      if (!response) {
        return;
      }
      if (response === 'CONNECTION') {
        showErrorModal();
        return;
      }
      if (response === 'NOT_UNIQUE_CODE') {
        setError('code', {
          message: t('common.errors.codeError', { code: formValues.code }),
        });
        return;
      }
      if (response === 'INVALID_CODE_CHARS') {
        setError('code', {
          message: t('common.validations.invalidCodeChars'),
        });
        return;
      }
      // El título/grado no es un campo puntual —puede faltar por los dos checks
      // "No se obtiene"—, así que va como aviso y no colgado de un input, que
      // además estaría deshabilitado y no se vería.
      if (response === 'TITLE_OR_DEGREE_REQUIRED') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.errors.titleOrDegreeRequired`),
        });
        return;
      }
      // Desconocido: NO inventamos la causa. Antes esto decía "el código ya se
      // encuentra en uso" para cualquier 400.
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.save'),
      });
    },
    [onSubmitProp, showErrorModal, setError, t, prefix],
  );

  useEffect(() => {
    let filtered: SelectOptionType[] = [];
    if (selectedAcademicLevel?.careerLevels) {
      filtered = [...formatOptions(selectedAcademicLevel.careerLevels)];
    }
    setFilteredCareerLevels(filtered);
  }, [selectedAcademicLevel]);

  const prevCampusRef = useRef(selectedAcademicLevel);
  useEffect(() => {
    const prevCampus = prevCampusRef.current;
    if (prevCampus?.value !== selectedAcademicLevel?.value) {
      setValue('careerLevel', null);
      prevCampusRef.current = selectedAcademicLevel;
    }
  }, [selectedAcademicLevel, setValue]);

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12}>
              <SectionTitle text={t(`${prefix}.data`)} />
              <Row>
                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <TextInput
                      name="code"
                      label={t(`${prefix}.planCode`)}
                      control={control}
                      rules={{
                        required: msgValidations.required,
                        maxLength: {
                          value: 50,
                          message: t('common.validations.maxLength', {
                            length: 50,
                          }),
                        },
                        pattern: {
                          value: CODE_PATTERN,
                          message: t('common.validations.invalidCodeChars'),
                        },
                      }}
                      disabled={readOnly}
                    />
                  </div>
                </Col>
                {!!siesEnabled && (
                  <Col xs={12} md={6}>
                    <div className="pb-3">
                      <TextInput
                        name="siesCode"
                        label={t('common.forms.siesCode')}
                        control={control}
                        disabled={readOnly}
                      />
                    </div>
                  </Col>
                )}
                {!!demreEnabled && (
                  <Col xs={12} md={6}>
                    <div className="pb-3">
                      <TextInput
                        name="demreCode"
                        label={t(`${prefix}.demreCode`)}
                        control={control}
                        rules={{
                          maxLength: {
                            value: 50,
                            message: t('common.validations.maxLength', {
                              length: 50,
                            }),
                          },
                        }}
                        disabled={readOnly}
                      />
                    </div>
                  </Col>
                )}
                <Col
                  xs={12}
                  md={codeColumnsUsed % 2 === 0 ? 12 : 6}
                  className="pb-3"
                >
                  <TextInput
                    name="name"
                    label={t('common.forms.name')}
                    control={control}
                    rules={{
                      required: msgValidations.required,
                      minLength: {
                        value: 5,
                        message: t('common.validations.minLength', {
                          length: 5,
                        }),
                      },
                      maxLength: {
                        value: 255,
                        message: t('common.validations.maxLength', {
                          length: 255,
                        }),
                      },
                    }}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} className="pb-3">
                  <Select
                    name="school"
                    label={t('common.forms.school')}
                    options={schools.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="modality"
                    label={t('common.forms.modality')}
                    options={modalities.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="periodType"
                    label={t('common.forms.periodType')}
                    options={periodTypes.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly || isUpdate}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="academicLevel"
                    label={t('common.forms.academicLevel')}
                    options={academicLevels.map((e) => ({
                      label: e.name,
                      value: e.id,
                      careerLevels: e.careerLevels,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="hasLaw20903CLRequirement"
                    label={t(`${prefix}.hasLaw20903CLRequirement`)}
                    options={lawRequirementOptions}
                    control={control}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <Select
                        name="careerLevel"
                        label={t('common.forms.careerLevel')}
                        options={careerLevels}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="pl-2">
                      <Tooltip
                        id="careerLevelTooltip"
                        text={t(`${prefix}.careerLevelTooltip`)}
                      >
                        <Icon name="information" />
                      </Tooltip>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={4} className="pb-3"></Col>
          </Row>
          <Row>
            <Col xs={12}>
              <SectionTitle
                text={t(`${prefix}.titlesAndDegrees.titleSectionTitle`)}
              />
              <Row className="align-items-center mb-3">
                <Col xs={12} md={8}>
                  <p className="mb-0">
                    {t(`${prefix}.titlesAndDegrees.titleGuidance`)}
                  </p>
                </Col>
                <Col
                  xs={12}
                  md={4}
                  className="d-flex justify-content-md-end mt-2 mt-md-0"
                >
                  <CheckInput
                    name="noTitleObtained"
                    label={t(`${prefix}.titlesAndDegrees.noTitleObtained`)}
                    control={control}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              <div className={styles.tadTableWrapper}>
                <table className={styles.tadTable}>
                  <thead>
                    <tr>
                      <th>{t(`${prefix}.titlesAndDegrees.genderColumn`)}</th>
                      <th>{t(`${prefix}.titlesAndDegrees.titleColumn`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.generic`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.requiredTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="degreeTitle"
                          control={control}
                          disabled={readOnly || noTitleObtained}
                          validate={(value) => {
                            if (noTitleObtained) {
                              return true;
                            }
                            if (!value || !value.trim()) {
                              return t(
                                `${prefix}.titlesAndDegrees.atLeastOneRequired`,
                              ) as string;
                            }
                            return true;
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.female`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.optionalTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="degreeTitleFemale"
                          control={control}
                          disabled={readOnly || noTitleObtained}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.male`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.optionalTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="degreeTitleMale"
                          control={control}
                          disabled={readOnly || noTitleObtained}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <SectionTitle
                text={t(`${prefix}.titlesAndDegrees.degreeSectionTitle`)}
              />
              <Row className="align-items-center mb-3">
                <Col xs={12} md={8}>
                  <p className="mb-0">
                    {t(`${prefix}.titlesAndDegrees.degreeGuidance`)}
                  </p>
                </Col>
                <Col
                  xs={12}
                  md={4}
                  className="d-flex justify-content-md-end mt-2 mt-md-0"
                >
                  <CheckInput
                    name="noAcademicDegree"
                    label={t(`${prefix}.titlesAndDegrees.noAcademicDegree`)}
                    control={control}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              <div className={styles.tadTableWrapper}>
                <table className={styles.tadTable}>
                  <thead>
                    <tr>
                      <th>{t(`${prefix}.titlesAndDegrees.genderColumn`)}</th>
                      <th>{t(`${prefix}.titlesAndDegrees.degreeColumn`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.generic`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.requiredTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="academicDegree"
                          control={control}
                          disabled={readOnly || noAcademicDegree}
                          validate={(value) => {
                            if (noAcademicDegree) {
                              return true;
                            }
                            if (!value || !value.trim()) {
                              return t(
                                `${prefix}.titlesAndDegrees.atLeastOneRequired`,
                              ) as string;
                            }
                            return true;
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.female`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.optionalTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="academicDegreeFemale"
                          control={control}
                          disabled={readOnly || noAcademicDegree}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <span className={styles.tadGenderLabel}>
                          {t(`${prefix}.titlesAndDegrees.male`)}
                        </span>{' '}
                        <span className={styles.tadGenderTag}>
                          {t(`${prefix}.titlesAndDegrees.optionalTag`)}
                        </span>
                      </td>
                      <td>
                        <GenderedInputCell
                          name="academicDegreeMale"
                          control={control}
                          disabled={readOnly || noAcademicDegree}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
          {siesEnabled && (
            <StudyPlanFormSies
              readOnly={readOnly}
              academicAreas={academicAreas}
              admissionRequirements={admissionRequirements}
            />
          )}

          {children({ isSubmitting, isDirty })}
        </Form>
      </FormProvider>
    </Fragment>
  );
};

export default StudyPlanForm;
